/* ============================================================================
   pdp-lp-ingredients.js
   Draws the leader lines joining each ingredient to the cup.

   WHY THIS IS JS AND NOT CSS
   The first attempt tilted a CSS pseudo-element by a fixed angle per position.
   That only ever approximates: the angle a line needs in order to actually
   point at the cup depends on how far the ingredient sits from the cup
   horizontally AND vertically, and those distances come from flexbox at render
   time — they change with viewport width, font size, label wrapping and the
   number of ingredients. A fixed angle is right for exactly one layout and
   visibly wrong everywhere else, which is what it looked like.

   So the lines are measured, not guessed: read the real rendered positions,
   aim every line at one convergence point inside the cup, and redraw whenever
   the layout changes.

   Degrades cleanly — with no JS there are simply no leader lines. They are
   decorative, and the diagram still reads as ingredients above a cup.

   This file ALSO drives the benefit reveal: clicking an ingredient opens its
   benefit panel below the cup. Accordion — one at a time, because the panels
   sit in a shared slot and two open at once would just stack.
   ========================================================================== */

(function () {
  'use strict';

  /* The point every line AIMS at, as a fraction of the cup's height. This only
     sets direction — no line is drawn all the way here. */
  var CONVERGE_Y = 0.34;

  /* The dash is CENTRED in the space between the ingredient and the cup: the
     gap above it and the gap below it are the same. Earlier the line started
     right under the label and stopped a long way short of the cup, which read
     as the line being wrong rather than as deliberate spacing.

     GAP is a FIXED distance in px, held back at each end — not a share of the
     run. That distinction is the whole behaviour: the outer ingredients sit
     further from the cup, so their run is longer, and with a constant gap the
     surplus all goes into the line. A proportional inset instead spent the
     surplus on bigger gaps, so every line came out nearly the same length no
     matter how far it had to travel.

     A constant gap is also the more correct reading of "equal spacing" — equal
     means the same number of pixels, not the same percentage. */
  var GAP = 16;

  /* Floor, for the unlikely case of a very short run (a tall label wrapping to
     three lines, say). Without it the hold-back could exceed the run and
     invert the line. */
  var MIN_LINE = 12;

  function draw(root) {
    var svg = root.querySelector('[data-ingredient-links]');
    var centre = root.querySelector('.pdp-lp-ingredients__center');
    var items = root.querySelectorAll('.pdp-lp-ingredients__item');
    if (!svg || !centre || !items.length) return;

    var path = svg.querySelector('path');
    if (!path) return;

    var box = root.getBoundingClientRect();
    if (!box.width || !box.height) return;

    // Match the SVG's user units to CSS pixels so measured coordinates can be
    // written straight in, with no scaling maths.
    svg.setAttribute('viewBox', '0 0 ' + box.width + ' ' + box.height);

    var c = centre.getBoundingClientRect();
    var targetX = c.left - box.left + c.width / 2;
    var targetY = c.top - box.top + c.height * CONVERGE_Y;
    var cupTop = c.top - box.top;

    var d = '';
    for (var i = 0; i < items.length; i++) {
      var r = items[i].getBoundingClientRect();
      var x = r.left - box.left + r.width / 2;
      var y = r.bottom - box.top;

      var dx = targetX - x;
      var dy = targetY - y;
      if (dy <= 0) continue;

      // The run is from the bottom of the ingredient to where this line would
      // meet the top of the cup — measured ALONG the line, not vertically, so
      // an angled outer line and a near-vertical inner one are compared like
      // for like.
      var tCup = (cupTop - y) / dy;
      if (tCup <= 0) continue;

      var runLength = Math.sqrt(dx * dx + dy * dy) * tCup;

      // Hold back a constant distance at each end; everything left over is
      // line. A longer run therefore draws a longer line.
      var gap = GAP;
      if (runLength - 2 * gap < MIN_LINE) {
        gap = Math.max(0, (runLength - MIN_LINE) / 2);
      }

      var t0 = tCup * (gap / runLength);
      var t1 = tCup * (1 - gap / runLength);
      if (t1 <= t0) continue;

      d += 'M' + (x + dx * t0).toFixed(1) + ',' + (y + dy * t0).toFixed(1) +
           'L' + (x + dx * t1).toFixed(1) + ',' + (y + dy * t1).toFixed(1) + ' ';
    }

    path.setAttribute('d', d.trim());
  }

  /* ---------------------------------------------------------- benefit reveal */

  /* The text column beside the cup is a two-state slot: the section's own copy,
     or one ingredient's benefit. Resolved from the trigger each time rather
     than cached, so it survives a theme-editor re-render of the section. */
  function toggleDefaultCopy(trigger, hide) {
    var wellness = trigger.closest('.pdp-lp-wellness');
    if (!wellness) return;
    var def = wellness.querySelector('[data-ing-default]');
    if (def) def.hidden = hide;
  }

  function panelFor(trigger) {
    var id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function closeTrigger(trigger) {
    var panel = panelFor(trigger);
    trigger.setAttribute('aria-expanded', 'false');
    toggleDefaultCopy(trigger, false);
    trigger.classList.remove('is-open');
    if (panel) {
      panel.hidden = true;
      panel.classList.remove('is-open');
    }
  }

  function openTrigger(trigger) {
    var panel = panelFor(trigger);
    trigger.setAttribute('aria-expanded', 'true');
    toggleDefaultCopy(trigger, true);
    trigger.classList.add('is-open');
    if (panel) {
      panel.hidden = false;
      requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
    }
  }

  /* ------------------------------------------------- "these are clickable" cue

     The bounce plays when the diagram first scrolls into view, not on page
     load: on a long landing page this band is well below the fold, so a
     load-time animation would finish unseen and the cue would be spent before
     anyone arrived.

     It plays at most twice and stops for good the moment someone clicks — an
     affordance hint that keeps hinting after it has been understood is just
     noise. The "+" badge carries the message from then on.

     Skipped entirely under prefers-reduced-motion. The badge still shows, so
     the section is no less usable; it just does not move. */

  var HINT_PLAYS = 2;
  var HINT_REPEAT_AFTER = 6000;

  function playHint(root) {
    var triggers = root.querySelectorAll('[data-ing-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].classList.remove('is-hinting');
    }
    // Force a reflow so re-adding the class restarts the animation rather than
    // being coalesced into a no-op.
    void root.offsetWidth;
    for (var j = 0; j < triggers.length; j++) {
      triggers[j].classList.add('is-hinting');
    }
  }

  function stopHinting(root) {
    root.dataset.hintDone = 'true';
    var triggers = root.querySelectorAll('[data-ing-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].classList.remove('is-hinting');
    }
  }

  function bindHint(root) {
    if (!root.querySelector('[data-ing-trigger]')) return;

    /* Move the benefit panels out from under the cup into the column beside it.
       Done here rather than in Liquid because the panels are progressive
       enhancement, so the snippet keeps them as one block and only the enhanced
       experience relocates them. */
    var wellnessRoot = root.closest('.pdp-lp-wellness');
    var slot = wellnessRoot ? wellnessRoot.querySelector('[data-ing-slot]') : null;
    var panelsEl = root.querySelector('.pdp-lp-ingredients__panels');
    if (slot && panelsEl && panelsEl.parentNode !== slot) {
      slot.appendChild(panelsEl);
      slot.classList.add('pdp-lp-wellness__text--has-panels');
    }

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver !== 'function') return;

    var plays = 0;
    var timer = null;

    var run = function () {
      if (root.dataset.hintDone === 'true') return;
      playHint(root);
      plays++;
      if (plays < HINT_PLAYS) {
        timer = setTimeout(run, HINT_REPEAT_AFTER);
      }
    };

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.disconnect();
        run();
      }
    }, { threshold: 0.4 });

    io.observe(root);

    root.addEventListener('click', function () {
      if (timer) clearTimeout(timer);
      io.disconnect();
      stopHinting(root);
    }, { once: true });
  }

  function bindBenefits(root, redraw) {
    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-ing-trigger]');
      if (!trigger || !root.contains(trigger)) return;

      var wasOpen = trigger.getAttribute('aria-expanded') === 'true';
      var all = root.querySelectorAll('[data-ing-trigger]');
      for (var i = 0; i < all.length; i++) closeTrigger(all[i]);
      if (!wasOpen) openTrigger(trigger);

      // Opening or closing a panel changes the diagram's height, which moves
      // nothing the lines depend on — but the ResizeObserver will fire anyway,
      // so redraw explicitly to keep the two in step within the same frame.
      redraw();
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = root.querySelector('[data-ing-trigger][aria-expanded="true"]');
      if (!open) return;
      closeTrigger(open);
      open.focus();
      redraw();
    });
  }

  function observe(root) {
    if (root.dataset.linksBound === 'true') return;
    root.dataset.linksBound = 'true';

    var scheduled = false;
    var redraw = function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        draw(root);
      });
    };

    redraw();
    bindBenefits(root, redraw);
    bindHint(root);

    // The cup and the cut-outs are lazy-loaded; their arrival changes the
    // geometry the lines are aimed at.
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) imgs[i].addEventListener('load', redraw, { once: true });
    }

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(redraw).observe(root);
    } else {
      window.addEventListener('resize', redraw);
    }

    // A late webfont swap reflows the labels, which moves every start point.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(redraw).catch(function () {});
    }
  }

  function init() {
    var roots = document.querySelectorAll('.pdp-lp-ingredients');
    for (var i = 0; i < roots.length; i++) observe(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
